import React, { useEffect, useMemo, useState } from 'react';
import {
  AuditTrail, Callout, CheckRow, ConfirmDialog, Field, FormRow, FormSection, ImpactList,
  NumberField, SaveBar, SideDrawer, SummaryStrip, Toolbar, formatThaiDate, ghostBtnCls,
  inputCls, secondaryBtnCls, undoReason, useToast, type SummaryStat,
  resolveLink, type NavLinkComponent, type AuditEntry,
} from '../components';
import { Icon } from '../components/base/Icon';
import {
  CURRENT_POINT_RULE, POINT_RULE_AUDIT, POINT_RULE_IMPACT, RESET_CYCLE_LABEL, RESET_CYCLE_NOTE,
  RESET_POLICY_LABEL, SCHEDULED_RULE_CHANGE, THAI_MONTHS, pointsFor, spendFor,
  type PointRule, type ResetCycle, type ResetPolicy,
} from './loyaltyData';

/**
 * PointRuleScreen — กติกาคะแนน (FR-ADM-24)
 *
 * อัตราแลกเปลี่ยน (บาทต่อคะแนน) · อายุคะแนน · รอบรีเซ็ตยอดสะสมรายปี
 *
 * หน้านี้ต่างจากหน้าอื่นในระบบ: มันมีค่าอยู่ไม่กี่ตัว แต่ทุกตัวกระทบสมาชิกทั้งฐาน
 * และผลกระทบมองไม่เห็นจากตัวเลขที่กรอก การเปลี่ยน "100" เป็น "50" ดูเหมือนแก้เลขตัวเดียว
 * แต่ความจริงคือต้นทุนคะแนนของบริษัทเพิ่มเท่าตัวและคะแนนคงค้างเพิ่มขึ้นทุกวัน
 *
 * จึงออกแบบรอบสามข้อ:
 *
 *   1. **พรีวิวอยู่ข้างช่องกรอก ไม่ใช่หลังกดบันทึก** — คอลัมน์ขวาคำนวณสด
 *      ทั้งทิศทาง "ซื้อ X บาท ได้กี่คะแนน" และทิศกลับ "อยากได้ Y คะแนน ต้องซื้อเท่าไร"
 *      ทิศกลับสำคัญกว่าที่คิด เพราะของรางวัลถูกตั้งราคาเป็นคะแนน
 *      คนตั้งกติกาต้องเห็นว่าคูปอง 100 บาทที่ตั้งไว้ 500 คะแนน = ต้องซื้อ 50,000 บาท
 *
 *   2. **ผลกระทบเขียนเป็นจำนวนคนและวันที่ ไม่ใช่คำเตือนลอย ๆ** — ImpactList บอกว่าใครกระทบ
 *      กี่คน ตั้งแต่เมื่อไร แยกตามค่าที่เปลี่ยน ไม่ใช่ข้อความ "การเปลี่ยนแปลงนี้มีผลกับสมาชิกทั้งหมด"
 *
 *   3. **ตั้งวันที่มีผลล่วงหน้าได้** — การเปลี่ยนอัตราคะแนนกลางเดือนทำให้ลูกค้าที่ซื้อวันเดียวกัน
 *      ได้คะแนนไม่เท่ากัน ซึ่งกลายเป็นเรื่องร้องเรียน ค่าตั้งต้นของหน้านี้จึงเป็น
 *      "มีผลวันที่ 1 ของเดือนถัดไป" ไม่ใช่ "มีผลทันที"
 *
 * ⛔ ไม่มีตัวเลขใดในหน้านี้ถูกฝังในโค้ด ทุกค่ามาจาก loyaltyData.ts และแก้ได้จากหน้าจอ
 *    (หลักการ No-code Configuration ข้อ 3.1 ของ SRS)
 */

type ScreenState = 'ready' | 'loading' | 'error' | 'no-access';

const SAMPLE_SPENDS = [300, 1_000, 2_500, 9_999];
const REVERSE_POINTS = [200, 500, 1_200, 6_000];

/** วันที่ 1 ของเดือนถัดไป — ค่าตั้งต้นของวันที่มีผล */
const firstOfNextMonth = (from = new Date('2026-08-22')): string => {
  const d = new Date(from.getFullYear(), from.getMonth() + 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const ratioText = (r: PointRule) =>
  `${r.spendPerPoint.toLocaleString('th-TH')} บาท = ${r.pointsEarned.toLocaleString('th-TH')} คะแนน`;

const expiryText = (r: PointRule) =>
  r.pointExpiryMonths === null ? 'ไม่หมดอายุ' : `${r.pointExpiryMonths.toLocaleString('th-TH')} เดือน`;

const cycleText = (r: PointRule) =>
  r.resetCycle === 'fiscal'
    ? `ปีงบประมาณ (เริ่ม ${THAI_MONTHS[r.fiscalStartMonth - 1]})`
    : RESET_CYCLE_LABEL[r.resetCycle];

export interface PointRuleScreenProps {
  /** host เป็นคนตัดสินว่ากดลิงก์แล้วไปไหน — ดู components/NavLink */
  linkComponent?: NavLinkComponent;
  initialState?: ScreenState;
  /** กติกาคะแนนจากชั้น API — ไม่ส่งมาจะใช้ข้อมูลจำลองในไฟล์ */
  rule?: PointRule;
  /** บันทึกกลับไปที่ backend */
  /** `reason` = เหตุผลจากกล่องยืนยัน — backend เก็บใน Audit Log */
  onSave?: (next: PointRule, reason?: string) => Promise<void>;
  /**
   * ฟิลด์ที่ backend ยังไม่มีที่เก็บ — หน้าจอจะขึ้นป้ายเตือนที่ฟิลด์เหล่านี้
   * เพื่อไม่ให้คนตั้งค่าเข้าใจผิดว่าแก้แล้วมีผล
   */
  unsupportedFields?: string[];
  /**
   * ตัวเลขคะแนนคงค้างจริงจาก loyalty-point-summary
   *
   * ไม่ส่งมา = ใช้ตัวเลขจำลอง POINT_RULE_IMPACT
   * ⚠️ เป็นคะแนน **reward** เท่านั้น ไม่รวมคะแนนสำหรับเลื่อนระดับ
   */
  /**
   * ประวัติการแก้กติกาคะแนนจาก AuditLog ของ backend
   *
   * ไม่ส่งมา = ใช้ข้อมูลจำลอง POINT_RULE_AUDIT (ใช้ตอนดูหน้านี้ใน /foundations)
   * ส่ง [] มา = โหลดแล้วแต่ยังไม่มีประวัติ — หน้าจอจะขึ้นข้อความว่าง ไม่ใช่ข้อมูลปลอม
   */
  auditEntries?: AuditEntry[];
  /**
   * ตัวเลขผลกระทบจาก API
   *   ไม่ส่งมา = ใช้ข้อมูลจำลอง (ใช้ตอนดูหน้านี้ใน /foundations)
   *   ส่ง `null` = ต่อ API แล้วแต่ดึงตัวเลขไม่ได้ — หน้าจอขึ้น "—" ไม่ใช่ตัวเลขปลอม
   */
  impact?: {
    totalOutstandingPoints: number;
    membersWithBalance: number;
    pointsExpiringSoon: number;
    /** จำนวนวันที่นับว่า "ใกล้หมดอายุ" — ใช้ทำป้ายกำกับให้ตรงความจริง */
    expireDays: number;
  } | null;
}

export const PointRuleScreen: React.FC<PointRuleScreenProps> = ({
  linkComponent,
  initialState = 'ready',
  auditEntries, rule: ruleProp, onSave, unsupportedFields = [], impact,
}) => {
  const Link = resolveLink(linkComponent);
  /**
   * ⚠️ ค่าที่ยังบันทึกไม่ได้ **ถูกซ่อนทั้งช่อง** ไม่ใช่แค่ขึ้นคำเตือน
   *    เดิมปล่อยให้กรอกได้แล้วเตือนไว้ข้างบน ซึ่งกลายเป็นกับดักทันทีที่เอาคำเตือนออก
   *    เหลือเฉพาะอัตราแลกเปลี่ยนคะแนนกับอายุคะแนน ซึ่งบันทึกได้จริง
   */
  const hideUnsupported = unsupportedFields.length > 0;

  const toast = useToast();
  const [screenState, setScreenState] = useState<ScreenState>(initialState);
  const [saved, setSaved] = useState<PointRule>(ruleProp ?? CURRENT_POINT_RULE);
  const [draft, setDraft] = useState<PointRule>(ruleProp ?? CURRENT_POINT_RULE);

  useEffect(() => { if (ruleProp) { setSaved(ruleProp); setDraft(ruleProp); } }, [ruleProp]);
  useEffect(() => { setScreenState(initialState); }, [initialState]);
  const [effectiveMode, setEffectiveMode] = useState<'now' | 'scheduled'>('scheduled');
  const [effectiveFrom, setEffectiveFrom] = useState<string>(firstOfNextMonth());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);

  /* พรีวิว — ยอดที่ผู้ใช้พิมพ์เองเพื่อทดลอง */
  const [testSpend, setTestSpend] = useState<number | ''>(1_000);
  const [testPoints, setTestPoints] = useState<number | ''>(500);

  const set = <K extends keyof PointRule>(key: K, value: PointRule[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  /* ------------------------------------------------ สิ่งที่เปลี่ยนไปจากค่าที่บันทึกไว้ */

  const changes = useMemo(() => {
    const out: Array<{ field: string; from: string; to: string; key: string }> = [];
    const push = (key: string, field: string, from: string, to: string) => {
      if (from !== to) out.push({ key, field, from, to });
    };
    push('rate', 'อัตราแลกเปลี่ยน', ratioText(saved), ratioText(draft));
    push('round', 'การปัดเศษ', saved.roundDown ? 'ปัดลง' : 'ปัดตามปกติ', draft.roundDown ? 'ปัดลง' : 'ปัดตามปกติ');
    push('carry', 'เศษบาทที่ไม่ครบฐาน', saved.carryRemainder ? 'ยกไปรอบถัดไป' : 'ตัดทิ้ง', draft.carryRemainder ? 'ยกไปรอบถัดไป' : 'ตัดทิ้ง');
    push('expiry', 'อายุคะแนน', expiryText(saved), expiryText(draft));
    push('notice', 'แจ้งเตือนก่อนคะแนนหมดอายุ', `${saved.expiryNoticeDays} วัน`, `${draft.expiryNoticeDays} วัน`);
    push('mode', 'วิธีนับคะแนนหมดอายุ', saved.expiryMode === 'fifo' ? 'เข้าก่อนหมดก่อน' : 'หมดยกล็อตตามรอบปี', draft.expiryMode === 'fifo' ? 'เข้าก่อนหมดก่อน' : 'หมดยกล็อตตามรอบปี');
    push('cycle', 'รอบรีเซ็ตยอดสะสม', cycleText(saved), cycleText(draft));
    push('policy', 'เมื่อถึงวันรีเซ็ต', RESET_POLICY_LABEL[saved.resetPolicy], RESET_POLICY_LABEL[draft.resetPolicy]);
    push('grace', 'ระยะผ่อนผัน', `${saved.graceDays} วัน`, `${draft.graceDays} วัน`);
    push('welcome', 'ส่วนลดแรกเข้า', `${saved.welcomeDiscountBaht} บาท เมื่อครบ ${saved.welcomeMinSpend.toLocaleString('th-TH')} บาท`, `${draft.welcomeDiscountBaht} บาท เมื่อครบ ${draft.welcomeMinSpend.toLocaleString('th-TH')} บาท`);
    return out;
  }, [saved, draft]);

  const changedKeys = useMemo(() => new Set(changes.map((c) => c.key)), [changes]);

  /* ------------------------------------------------------------- ตรวจค่าที่กรอก */

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (draft.spendPerPoint <= 0) e.spendPerPoint = 'ต้องมากกว่า 0 — ค่า 0 จะทำให้ทุกการซื้อได้คะแนนไม่จำกัด';
    if (draft.pointsEarned <= 0) e.pointsEarned = 'ต้องมากกว่า 0';
    if (draft.pointExpiryMonths !== null && draft.pointExpiryMonths < 1) e.pointExpiryMonths = 'ต้องอย่างน้อย 1 เดือน หรือเลือก "ไม่หมดอายุ"';
    if (draft.expiryNoticeDays < 1) e.expiryNoticeDays = 'ต้องอย่างน้อย 1 วัน';
    if (draft.pointExpiryMonths !== null && draft.expiryNoticeDays > draft.pointExpiryMonths * 30) {
      e.expiryNoticeDays = 'ยาวกว่าอายุคะแนน — ลูกค้าจะได้รับแจ้งเตือนตั้งแต่วันที่ได้คะแนน';
    }
    if (draft.resetPolicy === 'grace-period' && draft.graceDays < 1) e.graceDays = 'ต้องอย่างน้อย 1 วัน';
    if (draft.welcomeMinSpend > 0 && draft.welcomeDiscountBaht > draft.welcomeMinSpend * 0.5) {
      e.welcomeDiscountBaht = 'ส่วนลดเกินครึ่งของยอดขั้นต่ำ — ตรวจกับฝ่ายการเงินก่อน';
    }
    if (effectiveMode === 'scheduled' && !effectiveFrom) e.effectiveFrom = 'เลือกวันที่มีผลก่อน';
    return e;
  }, [draft, effectiveMode, effectiveFrom]);

  const blockingError = Object.entries(errors).find(([k]) => k !== 'expiryNoticeDays' && k !== 'welcomeDiscountBaht');

  /* --------------------------------------------------------------- พรีวิวผลลัพธ์ */

  const preview = useMemo(() => {
    const spend = typeof testSpend === 'number' ? testSpend : 0;
    const now = pointsFor(spend, saved);
    const next = pointsFor(spend, draft);
    return { spend, now, next, changed: now.points !== next.points };
  }, [testSpend, saved, draft]);

  const reverse = useMemo(() => {
    const p = typeof testPoints === 'number' ? testPoints : 0;
    return { points: p, now: spendFor(p, saved), next: spendFor(p, draft) };
  }, [testPoints, saved, draft]);

  /** ต้นทุนคะแนนคงค้างเปลี่ยนไปเท่าไรถ้าอัตราเปลี่ยน */
  const rateMultiplier = useMemo(() => {
    const before = saved.pointsEarned / saved.spendPerPoint;
    const after = draft.pointsEarned / draft.spendPerPoint;
    if (!Number.isFinite(after) || before === 0) return null;
    return after / before;
  }, [saved, draft]);

  /* --------------------------------------------------------------- ผลกระทบ */

  /**
   * ⚠️ ตัวเลขผลกระทบต้องมาจากของจริงเมื่อต่อ API แล้ว
   *    เคยใช้ตัวเลขจำลองทุกโหมด ทำให้กล่องยืนยันตอนบันทึกโชว์ "คำสั่งซื้อ/วัน" ที่ไม่มีอยู่จริง
   *    ตัวไหนยังไม่มี endpoint ให้ดึง ใช้ null แล้วหน้าจอจะไม่แสดงตัวเลขของบรรทัดนั้น
   */
  const impactItems = useMemo(() => {
    const live = impact !== undefined;
    const when = effectiveMode === 'now' ? 'ทันทีที่บันทึก' : formatThaiDate(effectiveFrom);
    const items: Array<{ who: string; count: number | null; unit?: string; when: string; detail?: string }> = [];
    if (changedKeys.has('rate') || changedKeys.has('round') || changedKeys.has('carry')) {
      items.push({
        who: 'คำสั่งซื้อที่เกิดหลังวันที่มีผล — ได้คะแนนตามอัตราใหม่',
        count: live ? null : POINT_RULE_IMPACT.ordersPerDay, unit: 'คำสั่งซื้อ/วัน', when,
        detail: 'คะแนนที่ให้ไปแล้วก่อนวันที่มีผลไม่ถูกคำนวณย้อนหลัง',
      });
    }
    if (changedKeys.has('expiry') || changedKeys.has('mode')) {
      items.push({
        who: 'สมาชิกที่มีคะแนนคงเหลือ — วันหมดอายุของคะแนนที่ถืออยู่ถูกคำนวณใหม่',
        count: live ? (impact?.membersWithBalance ?? null) : POINT_RULE_IMPACT.membersWithBalance, when,
        detail: live
          ? (impact
            ? `คะแนนคงค้างทั้งระบบ ${impact.totalOutstandingPoints.toLocaleString('th-TH')} คะแนน`
            : undefined)
          : `คะแนนคงค้างทั้งระบบ ${POINT_RULE_IMPACT.totalOutstandingPoints.toLocaleString('th-TH')} คะแนน`,
      });
    }
    if (changedKeys.has('notice')) {
      items.push({
        who: 'สมาชิกที่คะแนนใกล้หมดอายุ — ได้รับแจ้งเตือนตามระยะใหม่',
        count: null,
        when,
        detail: live
          ? (impact
            ? `คะแนนที่จะหมดอายุใน ${impact.expireDays} วัน ${impact.pointsExpiringSoon.toLocaleString('th-TH')} คะแนน`
            : undefined)
          : `คะแนนที่จะหมดอายุใน 90 วัน ${POINT_RULE_IMPACT.pointsExpiringIn90Days.toLocaleString('th-TH')} คะแนน`,
      });
    }
    if (changedKeys.has('cycle') || changedKeys.has('policy') || changedKeys.has('grace')) {
      items.push({
        who: 'สมาชิกทุกคน — วันรีเซ็ตยอดสะสมและวันปรับระดับขยับตามรอบใหม่',
        count: live ? null : POINT_RULE_IMPACT.membersAffectedByCycleChange, when,
        detail: 'ยอดสะสมที่ใช้คำนวณระดับสมาชิกถูกนับใหม่จากวันเริ่มรอบ',
      });
    }
    if (changedKeys.has('welcome')) {
      items.push({ who: 'สมาชิกใหม่ที่ยังไม่เคยสั่งซื้อ', count: null, when });
    }
    return items;
  }, [changedKeys, effectiveMode, effectiveFrom, impact]);

  /* ------------------------------------------------------------------ บันทึก */

  const doSave = (reason?: string) => {
    const before = saved;
    setSaved(draft);
    setConfirmOpen(false);

    void onSave?.(draft, reason).catch((err: unknown) => {
      setSaved(before);
      setDraft(before);
      toast.show({
        tone: 'error',
        message: `บันทึกไม่สำเร็จ — ${err instanceof Error ? err.message : 'กรุณาลองใหม่'} ค่าถูกคืนกลับเป็นของเดิมแล้ว`,
      });
    });

    toast.show({
      tone: 'success',
      message: effectiveMode === 'now'
        ? 'บันทึกกติกาคะแนนแล้ว มีผลทันที — บันทึกใน Audit Log'
        : `ตั้งกติกาใหม่ให้มีผลวันที่ ${formatThaiDate(effectiveFrom)} แล้ว — บันทึกใน Audit Log`,
      undo: { onUndo: () => { setSaved(before); setDraft(before); void onSave?.(before, undoReason(reason)); }, seconds: 10 },
    });
  };

  /* ตัวเลขจริงถ้าต่อ API แล้ว · undefined = โหมดสาธิตจึงใช้ของจำลอง · null = ดึงไม่ได้ */
  const im = impact === undefined
    ? {
        totalOutstandingPoints: POINT_RULE_IMPACT.totalOutstandingPoints,
        membersWithBalance: POINT_RULE_IMPACT.membersWithBalance,
        pointsExpiringSoon: POINT_RULE_IMPACT.pointsExpiringIn90Days,
        expireDays: 90,
      }
    : impact;

  const stats: SummaryStat[] = [
    { label: 'อัตราที่ใช้อยู่', value: ratioText(saved), icon: 'currency_exchange', tone: 'brand' },
    { label: 'คะแนนคงค้างทั้งระบบ', value: im ? im.totalOutstandingPoints.toLocaleString('th-TH') : '—', icon: 'savings' },
    { label: 'สมาชิกที่มีคะแนนคงเหลือ', value: im ? `${im.membersWithBalance.toLocaleString('th-TH')} คน` : '—', icon: 'groups' },
    {
      label: im ? `คะแนนที่จะหมดอายุใน ${im.expireDays} วัน` : 'คะแนนที่ใกล้หมดอายุ',
      value: im ? im.pointsExpiringSoon.toLocaleString('th-TH') : '—',
      icon: 'timer',
      tone: im && im.pointsExpiringSoon > 0 ? 'warning' : 'default',
    },
  ];

  if (screenState === 'no-access') {
    return (
      <div className="flex h-screen min-w-[var(--bo-min-width)] items-center justify-center bg-[var(--surface-0)] p-[var(--space-8)] font-[family-name:var(--font-thai)]">
        <Callout tone="warning" title="ไม่มีสิทธิ์เข้าถึงหน้านี้" className="max-w-[640px]">
          การแก้กติกาคะแนนจำกัดไว้เฉพาะบทบาท <strong>Admin — Marketing</strong> และ <strong>Super Admin</strong>
          ติดต่อผู้ดูแลระบบเพื่อขอสิทธิ์ พร้อมแจ้งเหตุผลที่ต้องใช้
        </Callout>
      </div>
    );
  }

  return (
    <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-100)] font-[family-name:var(--font-thai)]">
      <Toolbar
        title="กติกาคะแนน"
        end={
          <>
            <button type="button" onClick={() => setAuditOpen(true)} className={secondaryBtnCls}>
              <Icon name="history" size={17} />
              ประวัติการแก้ไข
              <span className="bo-num rounded-[var(--radius-pill)] bg-[var(--surface-100)] px-[var(--space-15)] text-[length:var(--text-table-sm)]">
                {POINT_RULE_AUDIT.length}
              </span>
            </button>
            <Link to="/reports/points" className={secondaryBtnCls} style={{ textDecoration: 'none' }}>
              <Icon name="analytics" size={17} />
              รายงานคะแนน
            </Link>
          </>
        }
      />

      <SummaryStrip stats={stats} />

      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 gap-[var(--space-4)] overflow-y-auto p-[var(--space-4)]">
          {/* ------------------------------------------------- คอลัมน์ซ้าย: ค่าที่ตั้งได้ */}
          <div className="flex min-w-0 flex-1 flex-col gap-[var(--space-4)]" style={{ maxWidth: 'calc(var(--bo-content-max) - 420px)' }}>
            
            {SCHEDULED_RULE_CHANGE && (
              <Callout tone="info" title="มีกติกาที่ตั้งไว้ให้มีผลในอนาคต">
                ค่าที่ตั้งไว้จะมีผลวันที่ {formatThaiDate(SCHEDULED_RULE_CHANGE.effectiveFrom)} —
                การบันทึกจากหน้านี้จะแทนที่ค่าที่ตั้งไว้เดิม
              </Callout>
            )}

            {/* 1. อัตราแลกเปลี่ยน */}
            <FormSection
              title="อัตราแลกเปลี่ยนคะแนน"
              requirement="FR-ADM-24"
              description="ยอดซื้อกี่บาทได้กี่คะแนน ใช้กับทุกช่องทางการซื้อ ทั้งแอปและหน้าร้าน"
            >
              <div className="flex flex-wrap items-start gap-[var(--space-4)]">
                <FormRow reserveMessageSpace label="ซื้อครบ" required error={errors.spendPerPoint} previousValue={changedKeys.has('rate') ? `${saved.spendPerPoint.toLocaleString('th-TH')} บาท` : undefined}>
                  <NumberField value={draft.spendPerPoint} onChange={(v) => set('spendPerPoint', v === '' ? 0 : v)} unit="บาท" min={1} width={168} ariaLabel="ยอดซื้อต่อหนึ่งหน่วยคะแนน (บาท)" />
                </FormRow>
                {/**
                  * ⚠️ เครื่องหมาย = ต้องใช้ **โครงเดียวกับช่องกรอก** ไม่ใช่เดาระยะด้วย padding
                  *    เดิมดัน padding ล่างให้พอดีกับความสูงของแถวข้าง ๆ ซึ่งผูกกับ line-height
                  *    ของฟอนต์ พอฝังใน host ที่ฟอนต์ต่างไปเล็กน้อย เครื่องหมายก็หลุดบรรทัดทันที
                  *    ตอนนี้จองความสูงของป้ายกำกับและช่องข้อความไว้เท่ากัน แล้วจัดกึ่งกลางตามช่องกรอก
                  */}
                <span aria-hidden="true" className="flex flex-col gap-[var(--space-1)]">
                  <span className="flex items-center gap-1 text-[length:var(--text-form)] font-[var(--weight-medium)]">&nbsp;</span>
                  <span className="flex min-h-[var(--hit-control)] items-center text-[length:var(--text-section)] text-[var(--ink-600)]">=</span>
                  <span
                    className="text-[length:var(--text-table-sm)] leading-[var(--leading-thai)]"
                    style={{ minHeight: 'calc(var(--text-table-sm) * var(--leading-thai))' }}
                  />
                </span>
                <FormRow reserveMessageSpace label="ได้" required error={errors.pointsEarned}>
                  <NumberField value={draft.pointsEarned} onChange={(v) => set('pointsEarned', v === '' ? 0 : v)} unit="คะแนน" min={1} width={152} ariaLabel="คะแนนที่ได้" />
                </FormRow>
              </div>

              {/*
                ⚠️ ข้อความนี้เคยอยู่ในแถว flex-wrap เดียวกับช่องกรอกด้านบน พร้อม flex-1
                   พอผู้ใช้พิมพ์จนอัตราเปลี่ยน ข้อความโผล่มาแย่งพื้นที่แล้วดันช่องกรอกขึ้นบรรทัดใหม่
                   ย้ายออกมาเป็นบรรทัดของตัวเอง และจองความสูงไว้ ช่องกรอกจึงอยู่นิ่ง
              */}
              <p
                className="m-0 text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--warning-text)]"
                style={{ minHeight: 'calc(var(--text-form) * var(--leading-thai))' }}
              >
                {rateMultiplier !== null && Math.abs(rateMultiplier - 1) > 0.001 && (
                  <>
                    <Icon name="trending_up" size={17} filled className="mr-1 inline align-[-3px]" />
                    ต้นทุนคะแนนต่อยอดขายเปลี่ยนเป็น <strong className="bo-num">{rateMultiplier.toFixed(2)}</strong> เท่าของอัตราเดิม
                  </>
                )}
              </p>

              {!hideUnsupported && (
              <div className="flex flex-col gap-[var(--space-2)]">
                <CheckRow
                  checked={draft.roundDown}
                  onChange={(v) => set('roundDown', v)}
                  label="ปัดเศษลง"
                  description={`ซื้อ ${(draft.spendPerPoint * 1.9).toLocaleString('th-TH')} บาท ได้ ${pointsFor(draft.spendPerPoint * 1.9, { ...draft, roundDown: true }).points.toLocaleString('th-TH')} คะแนน — ถ้าปิด จะได้ ${pointsFor(draft.spendPerPoint * 1.9, { ...draft, roundDown: false }).points.toLocaleString('th-TH')} คะแนน`}
                />
                <CheckRow
                  checked={draft.carryRemainder}
                  onChange={(v) => set('carryRemainder', v)}
                  label="เก็บเศษบาทที่ไม่ครบฐานไว้รวมกับการซื้อครั้งถัดไป"
                  description="ลูกค้าเข้าใจง่ายกว่าและไม่รู้สึกว่าเสียเปรียบ แต่ระบบต้องเก็บยอดเศษของทุกคนและแสดงในประวัติคะแนน"
                  disabled={!draft.roundDown}
                />
              </div>
              )}
            </FormSection>

            {/* 2. อายุคะแนน */}
            <FormSection
              title="อายุคะแนน"
              requirement="FR-ADM-24 · FR-PT-13"
              description="คะแนนที่ลูกค้าได้รับจะหมดอายุเมื่อไร และแจ้งเตือนล่วงหน้ากี่วัน"
            >
              <div className="flex flex-wrap items-end gap-[var(--space-4)]">
                <FormRow
                  reserveMessageSpace
                  label="คะแนนหมดอายุหลังได้รับ"
                  error={errors.pointExpiryMonths}
                  previousValue={changedKeys.has('expiry') ? expiryText(saved) : undefined}
                >
                  <span className="flex items-center gap-[var(--space-2)]">
                    <NumberField
                      value={draft.pointExpiryMonths ?? ''}
                      onChange={(v) => set('pointExpiryMonths', v === '' ? null : v)}
                      unit="เดือน" min={1} width={152}
                      disabled={draft.pointExpiryMonths === null}
                      ariaLabel="อายุคะแนน (เดือน)"
                    />
                    <button
                      type="button"
                      onClick={() => set('pointExpiryMonths', draft.pointExpiryMonths === null ? 24 : null)}
                      className={secondaryBtnCls}
                    >
                      {draft.pointExpiryMonths === null ? 'ตั้งอายุคะแนน' : 'ไม่หมดอายุ'}
                    </button>
                  </span>
                </FormRow>

                {!hideUnsupported && (
                <FormRow reserveMessageSpace label="แจ้งเตือนลูกค้าล่วงหน้า" error={errors.expiryNoticeDays}>
                  <NumberField
                    value={draft.expiryNoticeDays}
                    onChange={(v) => set('expiryNoticeDays', v === '' ? 0 : v)}
                    unit="วัน" min={1} width={144}
                    disabled={draft.pointExpiryMonths === null}
                    ariaLabel="แจ้งเตือนล่วงหน้า (วัน)"
                  />
                </FormRow>
                )}
              </div>

              {draft.pointExpiryMonths === null ? (
                <Callout tone="warning">
                  คะแนนที่ไม่หมดอายุจะกลายเป็นภาระผูกพันทางบัญชีที่โตขึ้นทุกปี
                  และเลิกใช้ภายหลังทำได้ยากเพราะกระทบคะแนนที่ลูกค้าถืออยู่แล้ว
                </Callout>
              ) : hideUnsupported ? null : (
                <fieldset className="m-0 flex flex-col gap-[var(--space-2)] border-0 p-0">
                  <legend className="mb-[var(--space-1)] p-0 text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--ink-900)]">
                    วิธีนับวันหมดอายุ
                  </legend>
                  {([
                    ['fifo', 'เข้าก่อนหมดก่อน (FIFO)', 'คะแนนแต่ละก้อนหมดอายุตามวันที่ได้รับ ลูกค้าเห็นวันหมดอายุแยกก้อนในประวัติ — ยุติธรรมที่สุด แต่ต้องอธิบายในแอป'],
                    ['annual-batch', 'หมดยกล็อตตามรอบปี', 'คะแนนที่ได้ในรอบปีเดียวกันหมดอายุวันเดียวกัน — ลูกค้าเข้าใจง่าย แต่คนที่ได้คะแนนปลายรอบเสียเปรียบ'],
                  ] as Array<[PointRule['expiryMode'], string, string]>).map(([id, label, note]) => (
                    <label key={id} className="flex cursor-pointer items-start gap-[var(--space-2)] rounded-[var(--radius-sm)] border border-[var(--line-200)] p-[var(--space-25)] hover:bg-[var(--blue-050)]">
                      <input
                        type="radio"
                        name="expiryMode"
                        checked={draft.expiryMode === id}
                        onChange={() => set('expiryMode', id)}
                        className="mt-[3px] h-4 w-4 shrink-0 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                      />
                      <span className="min-w-0">
                        <span className="block text-[length:var(--text-form-lg)] text-[var(--ink-900)]">{label}</span>
                        <span className="block text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">{note}</span>
                      </span>
                    </label>
                  ))}
                </fieldset>
              )}
            </FormSection>

            {/* 3. รอบรีเซ็ตยอดสะสมรายปี */}
            {!hideUnsupported && (
            <FormSection
              title="รอบรีเซ็ตยอดสะสมรายปี"
              requirement="FR-ADM-24 · BR-03"
              description="ยอดซื้อสะสมที่ใช้คำนวณระดับสมาชิกถูกนับใหม่เมื่อไร — ค่านี้กระทบวันปรับระดับของสมาชิกทุกคน"
            >
              <fieldset className="m-0 flex flex-col gap-[var(--space-2)] border-0 p-0">
                <legend className="sr-only">รอบการรีเซ็ต</legend>
                {(Object.keys(RESET_CYCLE_LABEL) as ResetCycle[]).map((c) => (
                  <label key={c} className="flex cursor-pointer items-start gap-[var(--space-2)] rounded-[var(--radius-sm)] border border-[var(--line-200)] p-[var(--space-25)] hover:bg-[var(--blue-050)]">
                    <input
                      type="radio"
                      name="resetCycle"
                      checked={draft.resetCycle === c}
                      onChange={() => set('resetCycle', c)}
                      className="mt-[3px] h-4 w-4 shrink-0 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-[var(--space-2)] text-[length:var(--text-form-lg)] text-[var(--ink-900)]">
                        {RESET_CYCLE_LABEL[c]}
                        {c === 'calendar' && (
                          <span className="rounded-[var(--radius-sm)] bg-[var(--success-bg)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--success-text)]">
                            แนะนำ
                          </span>
                        )}
                      </span>
                      <span className="block text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">{RESET_CYCLE_NOTE[c]}</span>
                      {c === 'fiscal' && draft.resetCycle === 'fiscal' && (
                        <span className="mt-[var(--space-2)] flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
                          เดือนเริ่มรอบ
                          <select
                            value={draft.fiscalStartMonth}
                            onChange={(e) => set('fiscalStartMonth', Number(e.target.value))}
                            className={inputCls}
                          >
                            {THAI_MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                          </select>
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </fieldset>

              <FormRow
                reserveMessageSpace
                label="เมื่อถึงวันรีเซ็ต ยอดสะสมและระดับสมาชิกทำอย่างไร"
                help="ยังไม่ได้กำหนดนโยบาย — ค่าที่เลือกไว้เป็นค่าตั้งต้น ไม่ใช่นโยบายที่ยืนยันแล้ว"
                previousValue={changedKeys.has('policy') ? RESET_POLICY_LABEL[saved.resetPolicy] : undefined}
              >
                <span className="flex flex-wrap items-center gap-[var(--space-3)]">
                  <select
                    value={draft.resetPolicy}
                    onChange={(e) => set('resetPolicy', e.target.value as ResetPolicy)}
                    className={inputCls}
                    style={{ minWidth: 340 }}
                  >
                    {(Object.keys(RESET_POLICY_LABEL) as ResetPolicy[]).map((p) => (
                      <option key={p} value={p}>{RESET_POLICY_LABEL[p]}</option>
                    ))}
                  </select>
                  {draft.resetPolicy === 'grace-period' && (
                    <span className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
                      ผ่อนผัน
                      <NumberField value={draft.graceDays} onChange={(v) => set('graceDays', v === '' ? 0 : v)} unit="วัน" min={1} width={128} ariaLabel="ระยะผ่อนผัน (วัน)" />
                    </span>
                  )}
                </span>
              </FormRow>
            </FormSection>
            )}

            {/* 4. ส่วนลดแรกเข้า */}
            {!hideUnsupported && (
            <FormSection
              title="ส่วนลดแรกเข้า"
              requirement="SRS 3.4"
              description="ส่วนลดที่สมาชิกใหม่ได้รับในการสั่งซื้อครั้งแรก"
            >
              <div className="flex flex-wrap items-end gap-[var(--space-4)]">
                <FormRow reserveMessageSpace label="ส่วนลด" error={errors.welcomeDiscountBaht}>
                  <NumberField value={draft.welcomeDiscountBaht} onChange={(v) => set('welcomeDiscountBaht', v === '' ? 0 : v)} unit="บาท" min={0} width={152} ariaLabel="ส่วนลดแรกเข้า (บาท)" />
                </FormRow>
                <FormRow reserveMessageSpace label="เมื่อซื้อครบ">
                  <NumberField value={draft.welcomeMinSpend} onChange={(v) => set('welcomeMinSpend', v === '' ? 0 : v)} unit="บาท" min={0} step={100} width={168} ariaLabel="ยอดขั้นต่ำของส่วนลดแรกเข้า" />
                </FormRow>
                <p className="m-0 flex-1 pb-[var(--space-15)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                  คิดเป็นส่วนลด{' '}
                  <strong className="bo-num">
                    {draft.welcomeMinSpend > 0 ? ((draft.welcomeDiscountBaht / draft.welcomeMinSpend) * 100).toFixed(1) : '—'}%
                  </strong>{' '}
                  ของยอดขั้นต่ำ
                </p>
              </div>
            </FormSection>
            )}

            {/* 5. วันที่มีผล */}
            <FormSection
              title="วันที่มีผล"
              description="การเปลี่ยนอัตราคะแนนกลางวันทำให้ลูกค้าที่ซื้อวันเดียวกันได้คะแนนไม่เท่ากัน จึงตั้งวันที่ล่วงหน้าเป็นค่าตั้งต้น"
            >
              <div className="flex flex-wrap items-start gap-[var(--space-3)]">
                {([
                  ['scheduled', 'ตั้งวันที่มีผลล่วงหน้า', 'แนะนำ — ใช้เวลาที่เหลือแจ้งลูกค้าและทีมหน้าร้านก่อน'],
                  ['now', 'มีผลทันทีที่บันทึก', 'ใช้เมื่อต้องแก้ค่าที่ตั้งผิดและกำลังสร้างความเสียหายอยู่'],
                ] as Array<['scheduled' | 'now', string, string]>).map(([m, label, note]) => (
                  <label key={m} className="flex min-w-[300px] flex-1 cursor-pointer items-start gap-[var(--space-2)] rounded-[var(--radius-sm)] border border-[var(--line-200)] p-[var(--space-25)] hover:bg-[var(--blue-050)]">
                    <input
                      type="radio"
                      name="effectiveMode"
                      checked={effectiveMode === m}
                      onChange={() => setEffectiveMode(m)}
                      className="mt-[3px] h-4 w-4 shrink-0 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                    />
                    <span className="min-w-0">
                      <span className="block text-[length:var(--text-form-lg)] text-[var(--ink-900)]">{label}</span>
                      <span className="block text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">{note}</span>
                      {m === 'scheduled' && effectiveMode === 'scheduled' && (
                        <span className="mt-[var(--space-2)] flex flex-wrap items-center gap-[var(--space-2)]">
                          <input
                            type="date"
                            value={effectiveFrom}
                            min="2026-08-23"
                            onChange={(e) => setEffectiveFrom(e.target.value)}
                            className={`${inputCls} bo-num`}
                            aria-label="วันที่ให้กติกาใหม่มีผล"
                          />
                          <span className="text-[length:var(--text-table-sm)] text-[var(--ink-700)]">{formatThaiDate(effectiveFrom)}</span>
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </FormSection>

            {/* 6. ผลกระทบ */}
            {impactItems.length > 0 && (
              <FormSection
                title="การเปลี่ยนแปลงนี้กระทบใคร ตั้งแต่เมื่อไร"
                description="ตัวเลขมาจากฐานสมาชิกปัจจุบัน — ตรวจให้ครบก่อนบันทึก"
              >
                <ImpactList items={impactItems} />
              </FormSection>
            )}

            <p className="m-0 pb-[var(--space-4)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-600)]">
              <Icon name="info" size={16} className="mr-1 inline align-[-3px]" />
              การแก้กติกาคะแนนทุกครั้งถูกบันทึกใน Audit Log พร้อมค่าเดิม ค่าใหม่ ผู้แก้ และเหตุผล (FR-ADM-37)
            </p>
          </div>

          {/* --------------------------------------------- คอลัมน์ขวา: พรีวิวผลลัพธ์ */}
          <aside className="sticky top-0 flex w-[396px] shrink-0 flex-col gap-[var(--space-3)] self-start">
            <section className="flex flex-col gap-[var(--space-3)] rounded-[var(--radius-md)] border-2 border-[var(--blue-200)] bg-[var(--surface-0)] p-[var(--space-3)]">
              <h2 className="m-0 flex items-center gap-[var(--space-2)] text-[length:var(--text-section)] font-[var(--weight-semibold)] text-[var(--ink-900)]">
                <Icon name="calculate" size={20} filled className="text-[var(--blue-700)]" />
                ตัวอย่างผลลัพธ์
              </h2>

              <div className="flex flex-col gap-[var(--space-2)] rounded-[var(--radius-sm)] bg-[var(--blue-050)] p-[var(--space-25)]">
                <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
                  ซื้อ
                  <NumberField value={testSpend} onChange={setTestSpend} unit="บาท" min={0} step={100} width={148} ariaLabel="ยอดซื้อที่ใช้ทดลอง" />
                </label>
                <p className="m-0 text-[length:var(--text-form-lg)] leading-[var(--leading-form)] text-[var(--ink-900)]">
                  จะได้{' '}
                  <strong className="bo-num text-[length:var(--text-page-title)] font-[var(--weight-bold)] text-[var(--blue-700)]" style={{ textAlign: 'left' }}>
                    {preview.next.points.toLocaleString('th-TH')}
                  </strong>{' '}
                  คะแนน
                </p>
                {preview.changed && (
                  <p className="m-0 flex items-center gap-[var(--space-15)] text-[length:var(--text-form)] text-[var(--warning-text)]">
                    <Icon name="swap_horiz" size={16} filled />
                    กติกาที่ใช้อยู่ตอนนี้ให้ <strong className="bo-num">{preview.now.points.toLocaleString('th-TH')}</strong> คะแนน
                  </p>
                )}
                {draft.roundDown && preview.next.remainder > 0 && (
                  <p className="m-0 text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                    เศษ {preview.next.remainder.toLocaleString('th-TH')} บาท{draft.carryRemainder ? ' ยกไปรวมกับการซื้อครั้งถัดไป' : ' ไม่ได้คะแนน'}
                  </p>
                )}
              </div>

              <table className="w-full border-collapse text-[length:var(--text-table)]">
                <caption className="sr-only">ตัวอย่างคะแนนที่ได้จากยอดซื้อต่าง ๆ</caption>
                <thead>
                  <tr className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">
                    <th scope="col" className="pb-[var(--space-1)] text-left font-[var(--weight-medium)]">ยอดซื้อ</th>
                    <th scope="col" className="pb-[var(--space-1)] text-right font-[var(--weight-medium)]">คะแนนใหม่</th>
                    <th scope="col" className="pb-[var(--space-1)] text-right font-[var(--weight-medium)]">คะแนนเดิม</th>
                  </tr>
                </thead>
                <tbody>
                  {SAMPLE_SPENDS.map((s) => {
                    const a = pointsFor(s, draft).points;
                    const b = pointsFor(s, saved).points;
                    return (
                      <tr key={s} className="border-t border-[var(--line-200)]">
                        <td className="bo-num py-[var(--space-1)] text-[var(--ink-900)]" style={{ textAlign: 'left' }}>{s.toLocaleString('th-TH')} บาท</td>
                        <td className="bo-num py-[var(--space-1)] font-[var(--weight-semibold)] text-[var(--ink-900)]">{a.toLocaleString('th-TH')}</td>
                        <td className="bo-num py-[var(--space-1)]" style={{ color: a === b ? 'var(--ink-600)' : 'var(--warning-text)' }}>{b.toLocaleString('th-TH')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>

            <section className="flex flex-col gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-3)]">
              <h3 className="m-0 flex items-center gap-[var(--space-2)] text-[length:var(--text-form-lg)] font-[var(--weight-semibold)] text-[var(--ink-900)]">
                <Icon name="swap_vert" size={18} className="text-[var(--blue-700)]" />
                คิดกลับ — ของรางวัลราคาเท่าไรในหน่วยบาท
              </h3>
              <p className="m-0 text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-600)]">
                ของรางวัลถูกตั้งราคาเป็นคะแนน ตัวเลขนี้บอกว่าลูกค้าต้องซื้อเท่าไรจึงแลกได้
              </p>
              <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
                <NumberField value={testPoints} onChange={setTestPoints} unit="คะแนน" min={0} step={50} width={148} ariaLabel="คะแนนที่ต้องการแลก" />
                = ซื้อ
                <strong className="bo-num text-[length:var(--text-section)] text-[var(--ink-900)]" style={{ textAlign: 'left' }}>
                  {reverse.next.toLocaleString('th-TH')}
                </strong>
                บาท
              </label>
              <ul className="m-0 flex list-none flex-col gap-[var(--space-05)] p-0">
                {REVERSE_POINTS.map((p) => (
                  <li key={p} className="flex items-baseline justify-between gap-[var(--space-2)] border-t border-[var(--line-200)] pt-[var(--space-1)] text-[length:var(--text-table)]">
                    <span className="bo-num text-[var(--ink-700)]" style={{ textAlign: 'left' }}>{p.toLocaleString('th-TH')} คะแนน</span>
                    <span className="bo-num font-[var(--weight-medium)] text-[var(--ink-900)]">{spendFor(p, draft).toLocaleString('th-TH')} บาท</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="flex flex-col gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-3)]">
              <h3 className="m-0 text-[length:var(--text-form-lg)] font-[var(--weight-semibold)] text-[var(--ink-900)]">ค่าที่ใช้อยู่ตอนนี้</h3>
              <div className="grid grid-cols-2 gap-[var(--space-25)]">
                <Field label="อัตราแลกเปลี่ยน">{ratioText(saved)}</Field>
                <Field label="อายุคะแนน">{expiryText(saved)}</Field>
                <Field label="รอบรีเซ็ต">{cycleText(saved)}</Field>
                <Field label="แจ้งเตือนล่วงหน้า">{saved.expiryNoticeDays} วัน</Field>
              </div>
              <button type="button" onClick={() => setAuditOpen(true)} className={ghostBtnCls}>
                <Icon name="history" size={15} />
                ดูว่าใครแก้ค่าเหล่านี้ล่าสุด
              </button>
            </section>
          </aside>
        </div>

        <SaveBar
          changeCount={changes.length}
          changes={changes.map(({ field, from, to }) => ({ field, from, to }))}
          onSave={() => setConfirmOpen(true)}
          onDiscard={() => setDraft(saved)}
          saveLabel={effectiveMode === 'now' ? 'บันทึกและใช้ทันที' : 'ตั้งให้มีผลตามวันที่'}
          blockedReason={blockingError ? blockingError[1] : undefined}
          impact={impactItems.length > 0 ? (
            <span>
              กระทบ{' '}
              <strong className="bo-num">{POINT_RULE_IMPACT.activeMembers.toLocaleString('th-TH')}</strong> สมาชิกที่ใช้งานอยู่ ·{' '}
              มีผล {effectiveMode === 'now' ? 'ทันทีที่บันทึก' : formatThaiDate(effectiveFrom)}
            </span>
          ) : undefined}
        />
      </div>

      {/* ------------------------------------------------ ประวัติการแก้ไข */}
      <SideDrawer
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        title="ประวัติการแก้กติกาคะแนน"
        subtitle="ทุกการแก้ไขถูกบันทึกพร้อมค่าเดิม ค่าใหม่ และเหตุผล (FR-ADM-37)"
        width="lg"
      >
        <AuditTrail entries={auditEntries ?? POINT_RULE_AUDIT} />
      </SideDrawer>

      {/* ------------------------------------------------------- ยืนยันก่อนบันทึก */}
      <ConfirmDialog
        open={confirmOpen}
        tone={changedKeys.has('rate') || changedKeys.has('cycle') ? 'danger' : 'default'}
        title="บันทึกกติกาคะแนน"
        description={
          <>
            <ul className="m-0 mb-[var(--space-3)] flex list-none flex-col gap-[var(--space-1)] p-0">
              {changes.map((c) => (
                <li key={c.key} className="flex flex-wrap items-baseline gap-[var(--space-2)] text-[length:var(--text-form)]">
                  <span className="text-[var(--ink-700)]">{c.field}</span>
                  <span className="text-[var(--ink-600)] line-through">{c.from}</span>
                  <Icon name="arrow_forward" size={14} className="text-[var(--ink-600)]" />
                  <span className="font-[var(--weight-semibold)] text-[var(--ink-900)]">{c.to}</span>
                </li>
              ))}
            </ul>
            <p className="m-0 font-[var(--weight-medium)] text-[var(--ink-900)]">
              มีผล {effectiveMode === 'now' ? 'ทันทีที่กดยืนยัน' : `วันที่ ${formatThaiDate(effectiveFrom)}`} ·
              กระทบสมาชิกที่ใช้งานอยู่ {POINT_RULE_IMPACT.activeMembers.toLocaleString('th-TH')} คน
            </p>
            {changedKeys.has('rate') && (
              <p className="m-0 mt-[var(--space-2)] text-[var(--error-text)]">
                คะแนนที่ให้ไปแล้วไม่ถูกคำนวณย้อนหลัง แต่ต้นทุนคะแนนของคำสั่งซื้อใหม่ทุกใบเปลี่ยนตั้งแต่วันที่มีผล
              </p>
            )}
          </>
        }
        confirmLabel={effectiveMode === 'now' ? 'บันทึกและใช้ทันที' : 'ตั้งเวลาให้มีผล'}
        requireReason
        reasonLabel="เหตุผลที่เปลี่ยนกติกา"
        reasonPresets={[
          'ได้ค่าที่ยืนยันจากลูกค้าในที่ประชุมแล้ว',
          'ปรับตามนโยบายควบคุมต้นทุนคะแนน',
          'แก้ค่าที่ตั้งผิดตั้งแต่แรก',
        ]}
        onConfirm={doSave}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default PointRuleScreen;

/* ============================================================================
   รองรับ:
   FR-ADM-24   ตั้งค่ากติกาคะแนน — อัตราแลกเปลี่ยน · อายุคะแนน · รอบรีเซ็ตยอดสะสมรายปี
   FR-PT-13    กำหนดวันหมดอายุของคะแนนและแจ้งเตือนล่วงหน้า
   BR-03       Tier คำนวณจากยอดซื้อสะสมในรอบปีและรีเซ็ตทุกปี
   SRS 3.1     ทุกค่าแก้ได้จากหน้าจอ ไม่ต้องแก้โปรแกรม (No-code Configuration)
   SRS 3.4     ค่าตั้งต้น 100 บาท = 1 คะแนน · ส่วนลดแรกเข้า 100 บาทเมื่อครบ 1,000 บาท
   FR-ADM-36   หน้านี้จำกัดสิทธิ์เฉพาะ Admin — Marketing และ Super Admin
   FR-ADM-37   ทุกการแก้ไขเข้า Audit Log พร้อมเหตุผลและค่าเดิม→ค่าใหม่
   FR-RPT-06   ตัวเลขคะแนนคงค้างในแถบสรุปมาจากรายงานเดียวกัน

   ประเด็นค้าง (⛔ ต้องได้คำตอบก่อนเปิดใช้จริง):
   — คำถามที่ยังค้างกับทีม backend นโยบายยอดสะสมและสิทธิ์คงค้าง ณ วันรีเซ็ตรอบปี
     (ตอนนี้ตั้งค่าตั้งต้นเป็น "คงระดับเดิมไว้ 90 วัน" ซึ่งเป็นค่าที่ทีมสมมติ)
   — อายุคะแนน 24 เดือนเป็นค่าที่ทีมสมมติ SRS ไม่ได้ระบุตัวเลข
   — วิธีนับคะแนนหมดอายุ (FIFO หรือยกล็อต) กระทบการแสดงประวัติคะแนนในแอป
     ถ้าเลือก FIFO ต้องออกแบบหน้าประวัติคะแนนใหม่ให้แสดงวันหมดอายุแยกก้อน
   — การเก็บเศษบาทยกไปรอบถัดไป ต้องยืนยันกับทีมพัฒนาว่าทำได้ในรอบนี้
   — เมื่อคืนสินค้า ระบบต้องหักคะแนนคืน (BR-14) — ยังไม่มีหน้าจอตั้งกติกาการหักคืน
   ============================================================================ */
